import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';

type CompositionRow = {
  correlation_id: string;
  thread_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number | null;
  outcome: string | null;
  originating_question_class: string | null;
  specialist_chain: unknown;
  owner_user_id: string | null;
  owner_email: string | null;
  location_id: string | null;
  mode: string | null;
  opportunistic_shortcut: boolean;
  slug: string | null;
  error: string | null;
};

const OUTCOME_OPTIONS = ['', 'success', 'error', 'partial', 'timeout'];

export default function Compositions() {
  const [search, setSearch] = useSearchParams();
  const since      = search.get('since')       || '';
  const until      = search.get('until')       || '';
  const specialist = search.get('specialist')  || '';
  const owner      = search.get('owner_email') || '';
  const slug       = search.get('slug')        || '';
  const outcome    = search.get('outcome')     || '';

  const [items, setItems] = useState<CompositionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null); setError(null);
    (async () => {
      try {
        const r = await api.listCompositions({
          since:       since      || undefined,
          until:       until      || undefined,
          specialist:  specialist || undefined,
          owner_email: owner      || undefined,
          slug:        slug       || undefined,
          outcome:     outcome    || undefined,
          limit:       100,
        });
        if (!cancelled) setItems(r.items);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.status === 403) {
          setError('Composition view is restricted to Cadence tier (T1/T2/T3).');
        } else if (err?.status === 503) {
          setError('PKNT supabase proxy is not configured on the Worker.');
        } else {
          setError(err?.message || 'Could not load compositions.');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [since, until, specialist, owner, slug, outcome]);

  function update(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value); else next.delete(key);
    setSearch(next, { replace: true });
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="font-display text-3xl font-medium text-ink">Compositions</h1>
        {items && (
          <span className="font-mono text-[11px] uppercase tracking-widest text-ink-light">
            {items.length} composition{items.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <p className="font-serif text-ink-light italic mb-6">
        Last 100 Rosie composition chains. Each row is one correlation_id —
        a single end-to-end question that may have fanned out across multiple
        specialists. Click a row to see the full event timeline.
      </p>

      <div className="flex flex-wrap gap-3 mb-6 pb-4 border-b border-rule">
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Since</span>
          <input
            type="datetime-local"
            value={since}
            onChange={(e) => update('since', e.target.value)}
            className="bg-paper border border-rule px-3 py-1.5 font-mono text-xs text-ink focus:outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Until</span>
          <input
            type="datetime-local"
            value={until}
            onChange={(e) => update('until', e.target.value)}
            className="bg-paper border border-rule px-3 py-1.5 font-mono text-xs text-ink focus:outline-none focus:border-accent"
          />
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Specialist</span>
          <input
            type="text"
            value={specialist}
            onChange={(e) => update('specialist', e.target.value)}
            placeholder="ask_backend_specialist"
            className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent w-56"
          />
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Owner email</span>
          <input
            type="text"
            value={owner}
            onChange={(e) => update('owner_email', e.target.value)}
            placeholder="owner@example.com"
            className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent w-64"
          />
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Playbook slug</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => update('slug', e.target.value)}
            placeholder="backend-to-ghl-4xx"
            className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent w-56"
          />
        </label>
        <label className="flex flex-col">
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">Outcome</span>
          <select
            value={outcome}
            onChange={(e) => update('outcome', e.target.value)}
            className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent min-w-[160px]"
          >
            {OUTCOME_OPTIONS.map((o) => <option key={o || 'all'} value={o}>{o || 'All outcomes'}</option>)}
          </select>
        </label>
      </div>

      {error && <p className="font-serif text-accent">{error}</p>}
      {!items && !error && (
        <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>
      )}
      {items && items.length === 0 && !error && (
        <p className="font-serif text-ink-light italic">No compositions match these filters.</p>
      )}
      {items && items.length > 0 && (
        <div className="overflow-x-auto border border-rule bg-paper">
          <table className="w-full font-mono text-[12px]">
            <thead>
              <tr className="bg-cream-dark text-left">
                <Th>Completed</Th>
                <Th>Duration</Th>
                <Th>Outcome</Th>
                <Th>Specialists</Th>
                <Th>Owner</Th>
                <Th>Playbook</Th>
                <Th>Correlation</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.correlation_id} className="border-t border-rule align-top hover:bg-cream-dark/30">
                  <td className="px-3 py-2 text-ink-light whitespace-nowrap" title={item.completed_at || ''}>
                    {item.completed_at
                      ? new Date(item.completed_at).toISOString().replace('T', ' ').slice(0, 19)
                      : <span className="text-ink-light">—</span>}
                  </td>
                  <td className="px-3 py-2 text-ink-soft whitespace-nowrap">
                    {item.duration_ms != null ? `${item.duration_ms} ms` : <span className="text-ink-light">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={outcomeBadgeClass(item.outcome)}>{item.outcome || '—'}</span>
                    {item.opportunistic_shortcut && (
                      <span
                        className="ml-2 inline-block px-2 py-0.5 bg-amber-50 text-amber-800 text-[10px]"
                        title="Opportunistic specialist→specialist shortcut (playbook hit)"
                      >
                        shortcut
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-ink-soft text-[11px] max-w-sm break-words">
                    {renderSpecialistChain(item.specialist_chain)}
                  </td>
                  <td className="px-3 py-2 text-ink-soft">
                    {item.owner_email || <span className="text-ink-light">—</span>}
                  </td>
                  <td className="px-3 py-2 text-ink-soft">
                    {item.slug || <span className="text-ink-light">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/compositions/${encodeURIComponent(item.correlation_id)}`}
                      className="text-accent hover:underline"
                      title={item.correlation_id}
                    >
                      {item.correlation_id.slice(0, 8)}…
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-ink-light">{children}</th>;
}

function outcomeBadgeClass(o: string | null): string {
  if (!o) return 'inline-block px-2 py-0.5 bg-cream-dark text-ink-light text-[11px]';
  if (o === 'success') return 'inline-block px-2 py-0.5 bg-emerald-50 text-emerald-800 text-[11px]';
  if (o === 'error' || o === 'timeout') return 'inline-block px-2 py-0.5 bg-red-50 text-red-800 text-[11px]';
  return 'inline-block px-2 py-0.5 bg-cream-dark text-ink-soft text-[11px]';
}

function renderSpecialistChain(chain: unknown): React.ReactNode {
  if (!Array.isArray(chain) || chain.length === 0) {
    return <span className="text-ink-light">—</span>;
  }
  return (
    <span>
      {chain.map((s, i) => (
        <span key={i}>
          {i > 0 && <span className="text-ink-light"> → </span>}
          <code>{String(s)}</code>
        </span>
      ))}
    </span>
  );
}
